import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Circle, Clock, AlertCircle, RefreshCw, Filter, ExternalLink, X, ArrowRight } from 'lucide-react';
import { tasksApi, usersApi } from '../services/api';
import './Tasks.css';

const TYPE_LABELS = {
  task:       { label: 'Deal Task',   color: '#3B82F6', bg: '#EFF6FF' },
  irl:        { label: 'IRL',         color: '#8B5CF6', bg: '#F5F3FF' },
  diligence:  { label: 'Diligence',   color: '#F59E0B', bg: '#FFFBEB' },
  annotation: { label: 'Annotation',  color: '#10B981', bg: '#ECFDF5' },
};

const PRIORITY_STYLES = {
  high:   { color: '#EF4444', bg: '#FEF2F2', label: 'High' },
  medium: { color: '#F59E0B', bg: '#FFFBEB', label: 'Medium' },
  low:    { color: '#6B7280', bg: '#F3F4F6', label: 'Low' },
  High:   { color: '#EF4444', bg: '#FEF2F2', label: 'High' },
  Medium: { color: '#F59E0B', bg: '#FFFBEB', label: 'Medium' },
  Low:    { color: '#6B7280', bg: '#F3F4F6', label: 'Low' },
};

const STATUS_DONE = ['completed', 'complete', 'Complete', 'Closed'];
const STATUS_IN_PROGRESS = ['in_progress', 'received', 'Partial'];

function statusIcon(status) {
  if (STATUS_DONE.includes(status)) return <CheckSquare size={15} color="#10B981" />;
  if (STATUS_IN_PROGRESS.includes(status)) return <Clock size={15} color="#F59E0B" />;
  return <Circle size={15} color="#D1D5DB" />;
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  return new Date(dueDate + 'T00:00:00') < new Date(new Date().toDateString());
}

function isDueToday(dueDate) {
  if (!dueDate) return false;
  return new Date(dueDate + 'T00:00:00').toDateString() === new Date().toDateString();
}

function getSubtitle(task) {
  if (!task.description) return null;
  try {
    const parsed = JSON.parse(task.description);
    if (parsed.type === 'annotation_link') {
      return `📎 ${parsed.documentName || 'Document annotation'}`;
    }
  } catch {}
  return task.description;
}

function TaskDetailModal({ task, onClose, onNavigate, onMarkComplete, completing }) {
  if (!task) return null;
  const typeInfo = TYPE_LABELS[task.type] || TYPE_LABELS.task;
  const subtitle = getSubtitle(task);

  return (
    <div className="tasks-modal-overlay" onClick={onClose}>
      <div className="tasks-modal" onClick={e => e.stopPropagation()}>
        <div className="tasks-modal-header">
          <div className="tasks-modal-type">
            <span className="tasks-badge" style={{ color: typeInfo.color, background: typeInfo.bg }}>
              {typeInfo.label}
            </span>
            {task.dealName && <span className="tasks-modal-deal">{task.dealName}</span>}
          </div>
          <button className="tasks-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="tasks-modal-body">
          <div className="tasks-modal-status" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span
              className={`tasks-status-icon${STATUS_DONE.includes(task.status) ? '' : ' tasks-status-icon--active'}`}
              onClick={(e) => !STATUS_DONE.includes(task.status) && onMarkComplete?.(e, task)}
              title={STATUS_DONE.includes(task.status) ? 'Completed' : 'Mark as complete'}
            >
              {completing?.has(task.id) ? <Clock size={15} color="#9CA3AF" /> : statusIcon(task.status)}
            </span>
            <span style={{ fontSize: 12, color: '#6B7280', textTransform: 'capitalize' }}>
              {task.status?.replace(/_/g, ' ') || 'pending'}
            </span>
          </div>
          <h2 className="tasks-modal-title">{task.title}</h2>
          {subtitle && <p className="tasks-modal-subtitle">{subtitle}</p>}

          <div className="tasks-modal-meta">
            {task.assignee && (
              <div className="tasks-modal-meta-item">
                <span className="tasks-modal-meta-label">Assignee</span>
                <span>{task.assignee}</span>
              </div>
            )}
            {task.dueDate && (
              <div className="tasks-modal-meta-item">
                <span className="tasks-modal-meta-label">Due</span>
                <span className={isOverdue(task.dueDate) && !STATUS_DONE.includes(task.status) ? 'tasks-due--overdue' : ''}>
                  {fmtDate(task.dueDate)}
                </span>
              </div>
            )}
            {task.priority && (
              <div className="tasks-modal-meta-item">
                <span className="tasks-modal-meta-label">Priority</span>
                <span>{task.priority}</span>
              </div>
            )}
          </div>
        </div>

        <div className="tasks-modal-footer">
          <button className="tasks-modal-cancel" onClick={onClose}>Close</button>
          {onNavigate && (
            <button className="tasks-modal-navigate" onClick={() => { onNavigate(task); onClose(); }}>
              Go to origin <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tasks({ currentUser, onNavigateToTask }) {
  const [tasks, setTasks] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completing, setCompleting] = useState(new Set());

  const handleMarkComplete = async (e, task) => {
    e.stopPropagation();
    if (STATUS_DONE.includes(task.status)) return;
    setCompleting(prev => new Set(prev).add(task.id));
    try {
      await tasksApi.markComplete(task.id);
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'completed' } : t));
      if (selectedTask?.id === task.id) setSelectedTask(t => ({ ...t, status: 'completed' }));
    } catch (err) {
      console.error('Failed to mark complete:', err);
    } finally {
      setCompleting(prev => { const s = new Set(prev); s.delete(task.id); return s; });
    }
  };

  // Filters
  const [filterAssignee, setFilterAssignee] = useState('me');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const currentUserName = currentUser?.name || currentUser?.full_name || currentUser?.email || '';

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAssignee === 'me' && currentUserName) params.assignee = currentUserName;
      else if (filterAssignee && filterAssignee !== 'all') params.assignee = filterAssignee;
      if (filterType !== 'all') params.type = filterType;
      if (filterPriority !== 'all') params.priority = filterPriority;

      const res = await tasksApi.list(params);
      setTasks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filterAssignee, filterType, filterPriority, currentUserName]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    usersApi.list().then(r => setOrgUsers(r.data || [])).catch(() => {});
  }, []);

  // Close modal on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelectedTask(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Client-side status filter
  const displayedTasks = tasks.filter(t => {
    if (filterStatus === 'active') return !STATUS_DONE.includes(t.status);
    if (filterStatus === 'completed') return STATUS_DONE.includes(t.status);
    return true;
  });

  const totalCount = tasks.length;
  const overdueCount = tasks.filter(t => !STATUS_DONE.includes(t.status) && isOverdue(t.dueDate)).length;
  const dueTodayCount = tasks.filter(t => !STATUS_DONE.includes(t.status) && isDueToday(t.dueDate)).length;
  const completedCount = tasks.filter(t => STATUS_DONE.includes(t.status)).length;

  return (
    <div className="tasks-page">
      {/* Header */}
      <div className="tasks-header">
        <div>
          <h1 className="tasks-title">Tasks</h1>
          <p className="tasks-subtitle">All assigned work across deals, IRL requests, and diligence</p>
        </div>
        <button className="tasks-refresh-btn" onClick={fetchTasks} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'tasks-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="tasks-stats">
        <div className="tasks-stat">
          <div className="tasks-stat-value">{totalCount}</div>
          <div className="tasks-stat-label">Total</div>
        </div>
        <div className="tasks-stat tasks-stat--overdue">
          <div className="tasks-stat-value">{overdueCount}</div>
          <div className="tasks-stat-label">Overdue</div>
        </div>
        <div className="tasks-stat tasks-stat--today">
          <div className="tasks-stat-value">{dueTodayCount}</div>
          <div className="tasks-stat-label">Due Today</div>
        </div>
        <div className="tasks-stat tasks-stat--done">
          <div className="tasks-stat-value">{completedCount}</div>
          <div className="tasks-stat-label">Completed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="tasks-filters">
        <Filter size={14} style={{ color: '#6B7280', flexShrink: 0 }} />

        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="me">My Tasks</option>
          <option value="all">All Assignees</option>
          {orgUsers.map(u => {
            const name = u.full_name || u.name || u.email;
            return <option key={u.id} value={name}>{name}</option>;
          })}
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="all">All Statuses</option>
        </select>

        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="task">Deal Tasks</option>
          <option value="irl">IRL Requests</option>
          <option value="diligence">Diligence</option>
          <option value="annotation">Annotations</option>
        </select>

        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Task list */}
      {loading ? (
        <div className="tasks-loading">Loading tasks...</div>
      ) : displayedTasks.length === 0 ? (
        <div className="tasks-empty">
          <CheckSquare size={40} style={{ color: '#D1D5DB', marginBottom: 12 }} />
          <p>No tasks found</p>
        </div>
      ) : (
        <div className="tasks-list">
          <div className="tasks-list-header">
            <span>Task</span>
            <span>Deal</span>
            <span>Type</span>
            <span>Priority</span>
            <span>Assignee</span>
            <span>Due Date</span>
            <span></span>
          </div>
          {displayedTasks.map(task => {
            const typeInfo = TYPE_LABELS[task.type] || TYPE_LABELS.task;
            const priorityStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
            const done = STATUS_DONE.includes(task.status);
            const overdue = !done && isOverdue(task.dueDate);
            const today = !done && isDueToday(task.dueDate);
            const subtitle = getSubtitle(task);

            return (
              <div key={task.id} className={`tasks-row ${done ? 'tasks-row--done' : ''}`}>
                <div className="tasks-row-title">
                  <div className="tasks-row-title-top">
                    <span
                      className={`tasks-status-icon${done ? '' : ' tasks-status-icon--active'}`}
                      onClick={(e) => handleMarkComplete(e, task)}
                      title={done ? 'Completed' : 'Mark as complete'}
                    >
                      {completing.has(task.id) ? <Clock size={15} color="#9CA3AF" /> : statusIcon(task.status)}
                    </span>
                    <span
                      className={`tasks-row-name ${done ? 'tasks-title-done' : ''}`}
                      onClick={() => onNavigateToTask?.(task)}
                      title="Go to origin"
                    >
                      {task.title}
                    </span>
                  </div>
                  {subtitle && <span className="tasks-row-desc">{subtitle}</span>}
                </div>
                <div className="tasks-row-deal">{task.dealName}</div>
                <div>
                  <span className="tasks-badge" style={{ color: typeInfo.color, background: typeInfo.bg }}>
                    {typeInfo.label}
                  </span>
                </div>
                <div>
                  <span className="tasks-badge" style={{ color: priorityStyle.color, background: priorityStyle.bg }}>
                    {priorityStyle.label}
                  </span>
                </div>
                <div className="tasks-row-assignee">{task.assignee || '—'}</div>
                <div className={`tasks-row-due ${overdue ? 'tasks-due--overdue' : today ? 'tasks-due--today' : ''}`}>
                  {task.dueDate ? (
                    <>
                      {overdue && <AlertCircle size={12} />}
                      {fmtDate(task.dueDate)}
                    </>
                  ) : '—'}
                </div>
                <div className="tasks-row-open">
                  <button
                    className="tasks-open-btn"
                    onClick={e => { e.stopPropagation(); setSelectedTask(task); }}
                    title="Open task details"
                  >
                    <ExternalLink size={13} />
                    Open
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TaskDetailModal
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        onNavigate={onNavigateToTask}
        onMarkComplete={handleMarkComplete}
        completing={completing}
      />
    </div>
  );
}
